/**
 * Vendas por Anúncio — contas da tela, separadas da UI.
 *
 * A régua de "Preço Médio" aqui é **receita ÷ unidades vendidas**, não receita ÷ pedidos.
 * É a conta do Upseller, conferida no print de 17/09/2026: R$ 1.123.895,27 ÷ 19.101 un =
 * R$ 58,84, o mesmo número que a tela dele mostra. Dividir por pedidos daria o ticket do
 * pedido, que é outra coisa e não bate com a referência.
 */

export type LinhaAnuncio = {
  /** id do anúncio no canal (MLB…, id Shopee). O bridge chama de `product_id`. */
  id: string
  descricao: string
  /** Título do anúncio, quando a fonte tem. Sem ele a linha cai no id do anúncio. */
  titulo: string | null
  /** SKU **do anúncio** (o que a conta cadastrou nele). Não confundir com `sku_description` de
   *  `margens`, que lista os SKUs do pedido e, em kit, os componentes individuais. */
  sku: string | null
  /** Miniatura da capa. null quando a fonte não tem — a célula mostra um lugar vazio,
   *  nunca uma imagem de outro anúncio. */
  thumbnail: string | null
  /** Link para a página do anúncio no canal. null quando não dá para montar. */
  link: string | null
  canais: string[]
  pedidos: number | null
  unidades: number
  receita: number
  /** receita ÷ unidades — null quando não vendeu unidade nenhuma. */
  precoMedio: number | null
}

/**
 * Link para a página do anúncio no marketplace.
 *
 * `permalink` do payload manda, quando existe. Sem ele, dá para montar o do Mercado Livre:
 * `MLB1234567890` → `produto.mercadolivre.com.br/MLB-1234567890`, que o ML redireciona para a
 * página certa.
 *
 * Shopee fica **sem link**: a URL dela exige o id da loja além do id do item
 * (`/product/<shopid>/<itemid>`), e o `shop_id` não vem nem no payload nem em
 * `ecommerce_financial.pedidos`. Chutar um id levaria a página errada — melhor texto puro.
 */
export function linkAnuncio(id: string, permalink?: string | null): string | null {
  const p = (permalink || '').trim()
  if (p.startsWith('http')) return p
  const m = /^MLB(\d+)$/i.exec((id || '').trim())
  if (m) return `https://produto.mercadolivre.com.br/MLB-${m[1]}`
  return null
}

export type ResumoAnuncios = {
  /** Anúncios com venda no período (o `total` da paginação, não o tamanho da página). */
  anuncios: number
  unidades: number
  receita: number
  precoMedio: number | null
}

export function precoMedio(receita: number, unidades: number): number | null {
  if (!Number.isFinite(receita) || !Number.isFinite(unidades) || unidades <= 0) return null
  return receita / unidades
}

type ProdutoBruto = {
  product_id?: string
  descricao?: string
  /** Contrato pedido ao backend (ver LEIA-ME da entrega): título e capa do anúncio. */
  titulo?: string
  sku?: string
  thumbnail?: string
  permalink?: string
  canais?: string[]
  quantidade?: number
  receita?: number
  pedidos?: number
}

/**
 * Normaliza o que vem de `/produtos/vendidos`.
 *
 * `pedidos` fica `null` quando o bridge não mandou o campo — a coluna mostra "—" em vez de
 * zero, porque zero pedido com unidade vendida seria uma afirmação falsa sobre o período.
 */
export function normalizarLinhas(produtos: ProdutoBruto[] | undefined): LinhaAnuncio[] {
  return (produtos || []).map((p) => {
    const unidades = Number(p.quantidade || 0)
    const receita = Number(p.receita || 0)
    const id = String(p.product_id || '')
    const titulo = (p.titulo || '').trim()
    return {
      id,
      descricao: String(p.descricao || p.product_id || '—'),
      titulo: titulo || null,
      sku: (p.sku || '').trim() || null,
      // `http2.mlstatic.com` serve as miniaturas do ML em http; forçar https evita o bloqueio
      // de conteúdo misto quando o BI estiver publicado.
      thumbnail: (p.thumbnail || '').trim().replace(/^http:\/\//, 'https://') || null,
      link: linkAnuncio(id, p.permalink),
      canais: Array.isArray(p.canais) ? p.canais.filter(Boolean).map(String) : [],
      pedidos: p.pedidos == null ? null : Number(p.pedidos),
      unidades,
      receita,
      precoMedio: precoMedio(receita, unidades),
    }
  })
}

export function montarResumo(
  resumo: { total_produtos?: number; receita_total?: number; quantidade_total?: number } | undefined,
  paginacaoTotal: number | undefined,
): ResumoAnuncios {
  const unidades = Number(resumo?.quantidade_total || 0)
  const receita = Number(resumo?.receita_total || 0)
  return {
    // `total_produtos` é o que o bridge contou; `paginacao.total` é o mesmo número por outro
    // caminho. Fica o primeiro que existir — sem somar os dois, que seria contar duas vezes.
    anuncios: Number(resumo?.total_produtos ?? paginacaoTotal ?? 0),
    unidades,
    receita,
    precoMedio: precoMedio(receita, unidades),
  }
}
